import { useI18n } from "hooks/i18n/i18n";
import React, { useEffect, useState } from "react";

import {
  isWindowLostNoticeFor,
  publishCustomerAccountResult,
  readCheckoutWindowName,
  readCustomerAccountCallback,
  retryCustomerAccountAuthorizeUrl,
  WINDOW_LOST_NOTICE_KEY,
} from "./customerAccountAuthorize";

/** 配信タブが窓を使い始めるまでの目安。これを過ぎたら、利用者に閉じてもらう */
const STUCK_AFTER_MS = 30_000;

type View =
  /** 配信タブが、この窓をチェックアウトへ遷移させるのを待っている */
  | "preparing"
  /** 配信タブから、この窓を使えないと伝えられた。ログインは済んでいる */
  | "windowLost"
  /** 待っても進まない（配信タブが閉じられた・再読み込みされたなど） */
  | "stuck";

/**
 * 購入クリックで開いた窓の中だけで進む。結果を配信タブへ渡すだけで、普段は窓を閉じない。
 * 開いた側が、同じ窓をそのままチェックアウトへ遷移させる。
 * 遷移させてもらえないときは、メッセージと閉じるボタンを出す。
 */
export const ShopifyCustomerAccountCallback = (): React.ReactElement | null => {
  const { t } = useI18n();
  const [view, setView] = useState<View | null>(null);

  useEffect(() => {
    const callback = readCustomerAccountCallback(
      window.location.search,
      localStorage
    );
    if (callback.kind === "retry") {
      const url = retryCustomerAccountAuthorizeUrl(
        window.location.origin,
        localStorage
      );
      if (url !== undefined) {
        window.location.assign(url);
        return;
      }
    }
    if (callback.kind === "done" || callback.kind === "retry") {
      setView("stuck");
      return;
    }

    // Shopify を経由すると窓の名前が消えるブラウザがある。
    // 名前が無いと、チェックアウトを開くときに別の窓として扱われてしまう
    const name = readCheckoutWindowName(localStorage, callback.state);
    if (name !== undefined) window.name = name;

    const onStorage = (event: StorageEvent) => {
      if (
        event.key === WINDOW_LOST_NOTICE_KEY &&
        isWindowLostNoticeFor(event.newValue, callback.state)
      ) {
        setView("windowLost");
        // スクリプトで開いた窓なら閉じられる。閉じられなければボタンを残す
        window.close();
      }
    };
    // 結果を渡す前に待ち始める。渡した直後の通知を取りこぼさない
    window.addEventListener("storage", onStorage);
    publishCustomerAccountResult(
      localStorage,
      callback.kind === "code"
        ? { kind: "code", code: callback.code, state: callback.state }
        : { kind: "failed", state: callback.state }
    );
    setView("preparing");
    const timer = window.setTimeout(() => {
      setView((current) => {
        if (current === "preparing") return "stuck";
        return current;
      });
    }, STUCK_AFTER_MS);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearTimeout(timer);
    };
  }, []);

  if (view === null) return null;
  if (view === "preparing") {
    return <p>{t("streaming.commerceModal.preparingCheckout")}</p>;
  }
  return (
    <div>
      <p>{t("streaming.commerceModal.closeWindowAndPurchaseAgain")}</p>
      <button type="button" onClick={() => window.close()}>
        {t("streaming.commerceModal.closeButton")}
      </button>
    </div>
  );
};
